import { Tag } from "primereact/tag";
import React, { useState } from "react";

import {
  CARC_CODES,
  GROUP_CODES,
  RARC_CODES,
} from "../../constants/adjustmentCodes";
import {
  IClaimEvent,
  ILineItem,
  ILineItemAdjustment,
  REMIT_STATUS_COLORS,
  REMIT_STATUS_LABELS,
  RemitStatusType,
} from "../../types/claim";

interface IEpisodeEventTimelineProps {
  events: IClaimEvent[];
  formatCurrency: (cents: number | null) => string;
}

const formatDate = (iso: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString();
};

/**
 * CLE-2166 — Per-event timeline for an episode's Remit Details tab.
 *
 * Renders one collapsed section per child event, ordered by BPR payment date
 * (the actual settlement date from the source 835's BPR segment). Each section
 * header shows the event date, RemitStatusType badge, CLP07, and event totals.
 * Clicking the header expands to show a compact line-items snippet.
 *
 * The full per-line audit (adjustments, references, remarks) is the existing
 * line-items table on the Expected Amounts tab; this timeline is the
 * chronological "what happened" view for the analyst.
 */
/**
 * Within-day ordering for events that share a BPR settlement date. The chronology
 * of a chain is conceptually: original adjudication first, then reversal of that
 * adjudication, then reprocess that supersedes it. When all three land on the
 * same day, surface them in that semantic order rather than ingestion order.
 */
const REMIT_STATUS_SORT_ORDER: Record<RemitStatusType, number> = {
  [RemitStatusType.PRIMARY]: 1,
  [RemitStatusType.PRIMARY_SPLIT]: 2,
  [RemitStatusType.DENIAL]: 3,
  [RemitStatusType.REVERSAL]: 4,
  [RemitStatusType.REPROCESS]: 5,
  [RemitStatusType.SECONDARY]: 6,
  [RemitStatusType.DUPLICATE_DENIAL]: 7,
};

const sortEventsForTimeline = (events: IClaimEvent[]): IClaimEvent[] =>
  [...events].sort((a, b) => {
    const aDate = a.bprPaymentDate ?? "";
    const bDate = b.bprPaymentDate ?? "";
    if (aDate !== bDate) return aDate < bDate ? -1 : 1;
    // Same BPR date — order by RemitStatusType (REVERSAL before REPROCESS, etc.).
    const aType = a.remitStatusType
      ? REMIT_STATUS_SORT_ORDER[a.remitStatusType as RemitStatusType] ?? 99
      : 99;
    const bType = b.remitStatusType
      ? REMIT_STATUS_SORT_ORDER[b.remitStatusType as RemitStatusType] ?? 99
      : 99;
    if (aType !== bType) return aType - bType;
    // Final tiebreaker — predecessor link. A REPROCESS that points at an earlier
    // event's CLP07 should come right after that earlier event, not the other way.
    if (a.supersedesPayerClaimControlNumber === b.payerClaimControlNumber)
      return 1;
    if (b.supersedesPayerClaimControlNumber === a.payerClaimControlNumber)
      return -1;
    return 0;
  });

/**
 * Line items store dollar values; the modal's formatCurrency expects cents.
 * Wrap so timeline cell values match the existing Remit Details table format.
 */
const formatLineDollars = (
  value: number | null | undefined,
  formatCurrency: (cents: number | null) => string
): string => {
  if (typeof value !== "number") return "-";
  return formatCurrency(Math.round(value * 100));
};

const PR_DEDUCTIBLE_CODE = "1";
const PR_COINSURANCE_CODE = "2";
const PR_COPAY_CODE = "3";
const PR_CODES = new Set([
  PR_DEDUCTIBLE_CODE,
  PR_COINSURANCE_CODE,
  PR_COPAY_CODE,
]);

const getPatientResponsibilityAmount = (
  line: ILineItem,
  prCode: string
): string => {
  const adj = line.adjustments?.find(
    (a) => a.group === "PR" && a.code === prCode
  );
  return adj && adj.amount > 0 ? `$${adj.amount.toFixed(2)}` : "-";
};

const formatAdjustments = (
  line: ILineItem,
  formatCurrency: (cents: number | null) => string
): string => {
  // Mirror the Remit Details table: PR-1/2/3 surface in dedicated columns,
  // so the Adjustments column lists everything else.
  const others = (line.adjustments ?? []).filter(
    (a: ILineItemAdjustment) => !(a.group === "PR" && PR_CODES.has(a.code))
  );
  if (others.length === 0) return "—";
  return others
    .map(
      (a: ILineItemAdjustment) =>
        `${a.group}-${a.code} ${formatCurrency(
          Math.round(Math.abs(a.amount) * 100)
        )}`
    )
    .join(", ");
};

const cellRight: React.CSSProperties = {
  padding: "0.25rem 0.5rem",
  textAlign: "right",
  whiteSpace: "nowrap",
};

const cellLeft: React.CSSProperties = {
  padding: "0.25rem 0.5rem",
  whiteSpace: "nowrap",
};

const cellAdjustments: React.CSSProperties = {
  padding: "0.25rem 0.5rem",
  fontSize: "0.75rem",
};

/**
 * Per-event glossary so an analyst reading a single event can decode the codes
 * without having to consult the aggregated legend below the Remit Details table
 * (which only reflects the canonical event's adjustments and may not include
 * codes that appeared in earlier reversed/superseded events).
 */
const collectAdjustmentCodes = (
  lineItems: ILineItem[]
): Array<{ group: string; code: string }> => {
  const seen = new Map<string, { group: string; code: string }>();
  lineItems.forEach((item) => {
    item.adjustments?.forEach((adj) => {
      const key = `${adj.group}-${adj.code}`;
      if (!seen.has(key)) seen.set(key, { group: adj.group, code: adj.code });
    });
  });
  return Array.from(seen.values()).sort((a, b) => {
    if (a.group !== b.group) return a.group.localeCompare(b.group);
    return a.code.localeCompare(b.code);
  });
};

const collectRemarkCodes = (lineItems: ILineItem[]): string[] => {
  const seen = new Set<string>();
  lineItems.forEach((item) => {
    item.remarks?.forEach((rem) => seen.add(rem.code));
  });
  return Array.from(seen).sort();
};

const glossaryStyle: React.CSSProperties = {
  marginTop: "0.75rem",
  padding: "0.75rem",
  background: "#f1f3f5",
  borderRadius: "4px",
  border: "1px solid #e0e0e0",
};

const glossaryTitleStyle: React.CSSProperties = {
  margin: "0 0 0.5rem 0",
  fontSize: "0.8rem",
  fontWeight: 600,
  color: "#495057",
};

const glossaryItemStyle: React.CSSProperties = {
  display: "flex",
  marginBottom: "0.35rem",
  fontSize: "0.8rem",
  lineHeight: 1.4,
};

const glossaryCodeStyle: React.CSSProperties = {
  fontWeight: 600,
  minWidth: "60px",
  color: "#212529",
};

const glossaryDescStyle: React.CSSProperties = {
  color: "#495057",
  flex: 1,
};

function EpisodeEventTimeline({
  events,
  formatCurrency,
}: IEpisodeEventTimelineProps) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  if (!events || events.length === 0) return null;

  const sortedEvents = sortEventsForTimeline(events);

  const toggle = (id: string) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <div style={{ marginTop: "1.5rem" }}>
      <h3
        style={{
          marginTop: 0,
          marginBottom: "0.75rem",
          fontSize: "16px",
          fontWeight: 600,
        }}
      >
        Event Timeline ({events.length})
      </h3>
      <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
        {sortedEvents.map((event) => {
          const isOpen = !!expanded[event.id];
          const lineCount = event.lineItems?.length ?? 0;
          return (
            <div
              key={event.id}
              style={{
                border: "1px solid #e0e0e0",
                borderRadius: "4px",
                background: "#fafafa",
              }}
            >
              <button
                type="button"
                onClick={() => toggle(event.id)}
                style={{
                  width: "100%",
                  background: "transparent",
                  border: "none",
                  padding: "0.75rem 1rem",
                  textAlign: "left",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "1rem",
                }}
              >
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                  }}
                >
                  <span style={{ fontWeight: 600, minWidth: "5.5rem" }}>
                    {formatDate(event.bprPaymentDate)}
                  </span>
                  {event.remitStatusType && (
                    <Tag
                      value={
                        REMIT_STATUS_LABELS[
                          event.remitStatusType as RemitStatusType
                        ]
                      }
                      severity={
                        REMIT_STATUS_COLORS[
                          event.remitStatusType as RemitStatusType
                        ]
                      }
                    />
                  )}
                  <span
                    style={{
                      fontFamily: "monospace",
                      fontSize: "0.85rem",
                      color: "#666",
                    }}
                  >
                    {event.payerClaimControlNumber || "—"}
                  </span>
                </span>
                <span
                  style={{
                    display: "flex",
                    gap: "1.5rem",
                    fontSize: "0.85rem",
                  }}
                >
                  {/* event.chargeAmount/claimAmount are stored in cents on the Claim row,
                      so they go straight into formatCurrency. Per-line values inside
                      the expanded table are dollars and use formatLineDollars. We show
                      claimAmount (allowed = paid + patient responsibility) here so the
                      timeline matches the "Remit Amount" in the Financial Summary —
                      both metrics describe the payer's adjudication of the claim, not
                      the cash that hit the bank. */}
                  <span>
                    Charges:{" "}
                    <strong>{formatCurrency(event.chargeAmount)}</strong>
                  </span>
                  <span>
                    Allowed:{" "}
                    <strong>{formatCurrency(event.claimAmount)}</strong>
                  </span>
                  <span>
                    {lineCount} line{lineCount === 1 ? "" : "s"}
                  </span>
                  <span>{isOpen ? "▾" : "▸"}</span>
                </span>
              </button>
              {isOpen && (
                <div
                  style={{
                    borderTop: "1px solid #e0e0e0",
                    padding: "0.75rem 1rem",
                  }}
                >
                  {lineCount === 0 ? (
                    <em style={{ color: "#888" }}>
                      No service lines (header-only adjudication).
                    </em>
                  ) : (
                    <table
                      style={{
                        width: "100%",
                        fontSize: "0.85rem",
                        borderCollapse: "collapse",
                      }}
                    >
                      <thead>
                        <tr style={{ textAlign: "left" }}>
                          <th style={cellLeft}>Code</th>
                          <th style={cellLeft}>Mod</th>
                          <th style={cellLeft}>Rev</th>
                          <th style={cellRight}>Units</th>
                          <th style={cellRight}>Billed</th>
                          <th style={cellRight}>Allowed</th>
                          <th style={cellRight}>Deduct</th>
                          <th style={cellRight}>Coins</th>
                          <th style={cellRight}>Copay</th>
                          <th style={cellLeft}>Adjustments</th>
                          <th style={cellRight}>Paid</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(event.lineItems ?? []).map(
                          (line: ILineItem, idx: number) => (
                            <tr
                              key={idx}
                              style={{ borderTop: "1px solid #eee" }}
                            >
                              <td style={cellLeft}>{line.cptCode}</td>
                              <td style={cellLeft}>{line.modifier ?? "—"}</td>
                              <td style={cellLeft}>
                                {line.revenueCode ?? "—"}
                              </td>
                              <td style={cellRight}>
                                {line.billedUnits ?? line.quantity ?? "—"}
                              </td>
                              <td style={cellRight}>
                                {formatLineDollars(
                                  line.lineCharge,
                                  formatCurrency
                                )}
                              </td>
                              <td style={cellRight}>
                                {formatLineDollars(
                                  line.lineAllowed,
                                  formatCurrency
                                )}
                              </td>
                              <td style={cellRight}>
                                {getPatientResponsibilityAmount(
                                  line,
                                  PR_DEDUCTIBLE_CODE
                                )}
                              </td>
                              <td style={cellRight}>
                                {getPatientResponsibilityAmount(
                                  line,
                                  PR_COINSURANCE_CODE
                                )}
                              </td>
                              <td style={cellRight}>
                                {getPatientResponsibilityAmount(
                                  line,
                                  PR_COPAY_CODE
                                )}
                              </td>
                              <td style={cellAdjustments}>
                                {formatAdjustments(line, formatCurrency)}
                              </td>
                              <td style={cellRight}>
                                {formatLineDollars(
                                  line.linePaid,
                                  formatCurrency
                                )}
                              </td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </table>
                  )}
                  {(() => {
                    const eventLineItems = event.lineItems ?? [];
                    const adjCodes = collectAdjustmentCodes(eventLineItems);
                    const remarkCodes = collectRemarkCodes(eventLineItems);
                    if (adjCodes.length === 0 && remarkCodes.length === 0)
                      return null;
                    return (
                      <div style={glossaryStyle}>
                        {adjCodes.length > 0 && (
                          <>
                            <h4 style={glossaryTitleStyle}>Adjustment Codes</h4>
                            {adjCodes.map(({ group, code }) => {
                              const description = CARC_CODES[code];
                              const groupDesc = GROUP_CODES[group];
                              return (
                                <div
                                  key={`${group}-${code}`}
                                  style={glossaryItemStyle}
                                >
                                  <span style={glossaryCodeStyle}>
                                    {group}-{code}
                                  </span>
                                  <span style={glossaryDescStyle}>
                                    {description ||
                                      `${groupDesc || group} adjustment`}
                                  </span>
                                </div>
                              );
                            })}
                          </>
                        )}
                        {remarkCodes.length > 0 && (
                          <>
                            <h4
                              style={{
                                ...glossaryTitleStyle,
                                marginTop: adjCodes.length > 0 ? "0.75rem" : 0,
                              }}
                            >
                              Remark Codes
                            </h4>
                            {remarkCodes.map((code) => {
                              const description = RARC_CODES[code];
                              return (
                                <div key={code} style={glossaryItemStyle}>
                                  <span style={glossaryCodeStyle}>{code}</span>
                                  <span style={glossaryDescStyle}>
                                    {description || "Remark code"}
                                  </span>
                                </div>
                              );
                            })}
                          </>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default EpisodeEventTimeline;
